import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Menu,
  Minus,
  Plus,
  Check,
} from 'lucide-react';
import MarkdownRenderer from './MessageBubble/MarkdownRenderer';
import { cn } from '@/lib/utils';
import { Tooltip } from './ui/tooltip';
import { saveAs } from 'file-saver';
import {
  exportMarkdownToDocx,
  type ExportOptions,
} from '@/utils/docx-exporter';

interface TocItem {
  id: string;
  text: string;
  level: number;
  children: TocItem[];
}

interface MarkdownPreviewPaneProps {
  content: string;
  filename: string;
}

/**
 * 解析 Markdown 内容，提取标题生成目录结构
 */
function parseTableOfContents(markdown: string): TocItem[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const items: TocItem[] = [];
  const stack: { item: TocItem; level: number }[] = [];

  let match;
  let index = 0;

  while ((match = headingRegex.exec(markdown)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    const id = `heading-${index++}-${text.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')}`;

    const item: TocItem = {
      id,
      text,
      level,
      children: [],
    };

    // 找到合适的父节点
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      items.push(item);
    } else {
      stack[stack.length - 1].item.children.push(item);
    }

    stack.push({ item, level });
  }

  return items;
}

/**
 * 给 Markdown 内容中的标题添加 id 属性
 * 同时添加两个锚点：
 * 1. 带索引的 ID（用于 TOC 导航，避免重复标题冲突）
 * 2. 简单格式的 ID（用于用户自定义的锚点链接，如 #introduction）
 */
function addHeadingIds(markdown: string): string {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  let index = 0;

  return markdown.replace(headingRegex, (_match, hashes, text) => {
    const textSlug = text
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const indexedId = `heading-${index++}-${textSlug}`;
    // 返回带有两个 HTML 锚点的格式：一个用于 TOC，一个用于用户自定义锚点
    return `${hashes} <a id="${indexedId}"></a><a id="${textSlug}"></a>${text}`;
  });
}

/**
 * TOC 项组件 - 支持展开/收起
 */
function TocItemComponent({
  item,
  activeId,
  onNavigate,
  depth = 0,
}: {
  item: TocItem;
  activeId: string;
  onNavigate: (id: string) => void;
  depth?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = item.children.length > 0;
  const isActive = activeId === item.id;

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 py-2 px-3 rounded cursor-pointer text-sm transition-colors',
          'hover:bg-gray-100',
          isActive && 'bg-blue-50 text-blue-700 font-medium'
        )}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
        onClick={() => onNavigate(item.id)}
      >
        {hasChildren && (
          <button
            className="p-0.5 hover:bg-gray-200 rounded"
            onClick={e => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronDown size={14} className="text-gray-500" />
            ) : (
              <ChevronRight size={14} className="text-gray-500" />
            )}
          </button>
        )}
        {!hasChildren && <span className="w-5" />}
        <span className="truncate" title={item.text}>
          {item.text}
        </span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {item.children.map(child => (
            <TocItemComponent
              key={child.id}
              item={child}
              activeId={activeId}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Markdown 预览面板组件
 * 支持目录导航、A4 纵向/横向布局、缩放功能
 */
export default function MarkdownPreviewPane({
  content,
  filename,
}: MarkdownPreviewPaneProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [zoom, setZoom] = useState(100);
  const [layout, setLayout] = useState<'portrait' | 'landscape'>('portrait');
  const [copied, setCopied] = useState(false);
  const [treatHrAsPageBreak, setTreatHrAsPageBreak] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  // 🔥 移动端检测并设置默认缩放为 50%
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 1024;
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // 如果是移动设备，设置缩放为 50%
      if (isMobileUA || (isSmallScreen && isTouchDevice)) {
        setZoom(50);
      }
    };

    checkMobile();
  }, []);

  // 解析目录
  const toc = useMemo(() => parseTableOfContents(content), [content]);

  // 处理后的内容（添加标题 ID）
  const processedContent = useMemo(() => addHeadingIds(content), [content]);

  // 导航到指定标题（使用即时滚动，避免卡顿感）
  const handleNavigate = useCallback((id: string) => {
    setActiveHeadingId(id);
    const element = document.getElementById(id);
    const container = contentRef.current;
    if (element && container) {
      // 计算元素相对于滚动容器的位置
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop =
        container.scrollTop + (elementRect.top - containerRect.top) - 80;
      // 使用 instant 行为实现无感知的即时跳转
      container.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'instant',
      });
    }
  }, []);

  // 下载为 Word 文档（使用 AST 解析，保留完整样式）
  const handleDownloadWord = useCallback(async () => {
    try {
      const exportOptions: ExportOptions = {
        treatHrAsPageBreak,
        orientation: layout,
      };
      const blob = await exportMarkdownToDocx(
        content,
        undefined,
        exportOptions
      );
      const docFilename = filename.replace(/\.(md|markdown)$/i, '') + '.docx';

      // 尝试使用 File System Access API 让用户选择保存位置
      if ('showSaveFilePicker' in window) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: docFilename,
            types: [
              {
                description: 'Word Document',
                accept: {
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    ['.docx'],
                },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
        } catch (err: unknown) {
          // 用户取消了保存对话框，或者 API 不可用
          if (err instanceof Error && err.name !== 'AbortError') {
            // 回退到自动下载
            saveAs(blob, docFilename);
          }
        }
      } else {
        // 浏览器不支持 File System Access API，使用传统方式
        saveAs(blob, docFilename);
      }
    } catch (error) {
      console.error('Failed to generate Word document:', error);
    }
  }, [content, filename, treatHrAsPageBreak, layout]);

  // 复制内容到剪贴板（保留格式，为 Word 优化）
  const handleCopyFormatted = useCallback(async () => {
    if (!paperRef.current) return;

    try {
      // 克隆 DOM 节点以便修改
      const clonedContent = paperRef.current.cloneNode(true) as HTMLElement;

      // 移除 Page Break 标记（包含 "Page Break" 文字的元素）
      const pageBreakElements = clonedContent.querySelectorAll('hr, .my-4');
      pageBreakElements.forEach(el => {
        // 检查是否是 Page Break 容器（包含 "Page Break" 文字）
        if (el.textContent?.includes('Page Break')) {
          el.remove();
        }
      });
      // 移除包含 Page Break 文字的 div 容器
      const allDivs = clonedContent.querySelectorAll('div');
      allDivs.forEach(div => {
        if (div.textContent?.trim() === 'Page Break') {
          div.remove();
        }
      });
      // 移除包含 Page Break 的 span
      const spans = clonedContent.querySelectorAll('span');
      spans.forEach(span => {
        if (span.textContent?.includes('Page Break')) {
          // 移除整个父容器（通常是 div）
          const parent = span.closest('div.my-4, div.flex');
          if (parent) {
            parent.remove();
          } else {
            span.remove();
          }
        }
      });

      // ===== 处理 CodeBlock 组件结构 =====
      // CodeBlock 组件结构: div.my-3.rounded-lg > div.bg-gray-100 (header) + SyntaxHighlighter
      // 需要将其转换为 Word 可识别的简单 pre 结构
      const codeBlockWrappers = clonedContent.querySelectorAll(
        'div.rounded-lg.overflow-hidden'
      );
      codeBlockWrappers.forEach(wrapper => {
        const wrapperEl = wrapper as HTMLElement;
        // 检查是否是代码块容器（有 border 类）
        if (
          wrapperEl.classList.contains('border') ||
          wrapperEl.className.includes('border-gray')
        ) {
          // 获取代码内容 - 在 SyntaxHighlighter 渲染的 pre > code 中
          const codeEl = wrapperEl.querySelector('pre code');
          const preEl = wrapperEl.querySelector('pre');
          if (codeEl || preEl) {
            const codeText = codeEl?.textContent || preEl?.textContent || '';
            // 获取语言标签
            const langSpan = wrapperEl.querySelector(
              '.bg-gray-100 span, div > span.text-xs'
            );
            const language = langSpan?.textContent?.trim() || 'text';

            // 创建 Word 兼容的代码块结构
            const newPre = document.createElement('pre');
            newPre.style.backgroundColor = '#f8f8f8';
            newPre.style.border = '1px solid #d1d5db';
            newPre.style.borderRadius = '0';
            newPre.style.padding = '12px';
            newPre.style.marginTop = '12pt';
            newPre.style.marginBottom = '12pt';
            newPre.style.fontFamily = 'Consolas, Monaco, monospace';
            newPre.style.fontSize = '9pt';
            newPre.style.lineHeight = '1.5';
            newPre.style.whiteSpace = 'pre-wrap';
            newPre.style.wordBreak = 'break-all';
            newPre.style.color = '#1f2937';
            newPre.style.overflowX = 'auto';

            // 添加语言标签作为注释
            if (language && language !== 'text') {
              const langComment = document.createElement('div');
              langComment.style.backgroundColor = '#e5e7eb';
              langComment.style.padding = '4px 12px';
              langComment.style.fontSize = '9pt';
              langComment.style.fontFamily = 'Arial, sans-serif';
              langComment.style.fontWeight = 'bold';
              langComment.style.color = '#4b5563';
              langComment.style.marginBottom = '0';
              langComment.style.borderBottom = '1px solid #d1d5db';
              langComment.textContent = language.toUpperCase();
              wrapperEl.parentNode?.insertBefore(langComment, wrapperEl);
            }

            const newCode = document.createElement('code');
            newCode.style.fontFamily = 'Consolas, Monaco, monospace';
            newCode.style.fontSize = '9pt';
            newCode.style.backgroundColor = 'transparent';
            newCode.textContent = codeText;

            newPre.appendChild(newCode);
            wrapperEl.replaceWith(newPre);
          }
        }
      });

      // 将 Tailwind CSS 类转换为 Word 兼容的内联样式和 HTML 属性
      // Word 不识别 CSS 类，只能识别内联样式和特定 HTML 属性

      // 处理表格 - 添加 Word 兼容的属性
      const tables = clonedContent.querySelectorAll('table');
      tables.forEach(table => {
        table.setAttribute('border', '1');
        table.setAttribute('cellpadding', '8');
        table.setAttribute('cellspacing', '0');
        table.setAttribute('align', 'center'); // 表格居中
        table.style.borderCollapse = 'collapse';
        table.style.width = 'auto';
        table.style.marginTop = '12pt'; // 段前间距
        table.style.marginBottom = '12pt'; // 段后间距
        table.style.marginLeft = 'auto';
        table.style.marginRight = 'auto';
      });

      // 处理表头单元格
      const thCells = clonedContent.querySelectorAll('th');
      thCells.forEach(th => {
        th.style.backgroundColor = '#f3f4f6'; // gray-100
        th.style.fontWeight = 'bold';
        th.style.padding = '8px';
        th.style.border = '1px solid #d1d5db'; // gray-300
        th.style.textAlign = 'left';
        th.style.fontSize = '10.5pt';
        th.style.fontFamily = 'Arial, sans-serif';
        th.style.color = '#1f2937';
      });

      // 处理表格数据单元格
      const tdCells = clonedContent.querySelectorAll('td');
      tdCells.forEach(td => {
        td.style.padding = '8px';
        td.style.border = '1px solid #d1d5db'; // gray-300
        td.style.fontSize = '10.5pt';
        td.style.fontFamily = 'Arial, sans-serif';
        td.style.color = '#374151';
        td.style.lineHeight = '1.6';
      });

      // 处理表格行的交替背景色
      const tbodyRows = clonedContent.querySelectorAll('tbody tr');
      tbodyRows.forEach((tr, index) => {
        if (index % 2 === 1) {
          (tr as HTMLElement).style.backgroundColor = '#f9fafb'; // gray-50
        }
      });

      // 处理 thead 背景色
      const theadRows = clonedContent.querySelectorAll('thead');
      theadRows.forEach(thead => {
        (thead as HTMLElement).style.backgroundColor = '#f9fafb';
      });

      // 处理段落 - MarkdownRenderer 使用 div.mb-3 而不是 p
      // 将段落 div 转换为 p 标签，以获得更好的 Word 兼容性
      const paragraphDivs = clonedContent.querySelectorAll('div');
      paragraphDivs.forEach(div => {
        const el = div as HTMLElement;
        // 检查是否是段落容器（有 mb-3 或 leading-relaxed 类）
        if (
          el.classList.contains('mb-3') ||
          el.classList.contains('leading-relaxed')
        ) {
          // 排除特殊容器（代码块、表格等）
          if (
            !el.closest('pre') &&
            !el.closest('table') &&
            !el.closest('blockquote') &&
            !el.querySelector('pre') &&
            !el.querySelector('table') &&
            !el.querySelector('div') // 排除有嵌套 div 的容器
          ) {
            // 将 div 转换为 p 标签
            const p = document.createElement('p');
            p.innerHTML = el.innerHTML;
            p.style.fontSize = '10.5pt';
            p.style.fontFamily = 'Arial, sans-serif';
            p.style.color = '#374151';
            p.style.lineHeight = '1.8';
            p.style.marginTop = '6pt';
            p.style.marginBottom = '6pt';
            p.style.textAlign = 'justify';
            // 添加 mso 样式属性以确保 Word 识别
            p.setAttribute(
              'style',
              p.getAttribute('style') +
                '; mso-margin-top-alt: 6pt; mso-margin-bottom-alt: 6pt; mso-line-height-rule: exactly;'
            );
            el.replaceWith(p);
          }
        }
      });

      // 处理 p 标签（包括原有的和刚转换的）
      const paragraphs = clonedContent.querySelectorAll('p');
      paragraphs.forEach(p => {
        const el = p as HTMLElement;
        if (!el.style.fontSize) {
          el.style.fontSize = '10.5pt';
          el.style.fontFamily = 'Arial, sans-serif';
          el.style.color = '#374151';
          el.style.lineHeight = '1.8';
          el.style.marginTop = '6pt';
          el.style.marginBottom = '6pt';
          el.style.textAlign = 'justify';
          el.setAttribute(
            'style',
            el.getAttribute('style') +
              '; mso-margin-top-alt: 6pt; mso-margin-bottom-alt: 6pt; mso-line-height-rule: exactly;'
          );
        }
      });

      // 处理代码块 - 包括已转换的和原生的
      const codeBlocks = clonedContent.querySelectorAll('pre');
      codeBlocks.forEach(pre => {
        // 如果还没有设置样式（不是从 CodeBlock 转换来的）
        if (!pre.style.backgroundColor) {
          pre.style.backgroundColor = '#f8f8f8';
          pre.style.padding = '12px';
          pre.style.fontFamily = 'Consolas, Monaco, monospace';
          pre.style.fontSize = '9pt';
          pre.style.whiteSpace = 'pre-wrap';
          pre.style.border = '1px solid #d1d5db';
          pre.style.color = '#1f2937';
          pre.style.lineHeight = '1.5';
          pre.style.marginTop = '12pt';
          pre.style.marginBottom = '12pt';
        }
      });

      // 处理内联代码 - MarkdownRenderer 使用 code.bg-gray-100
      const inlineCodes = clonedContent.querySelectorAll('code:not(pre code)');
      inlineCodes.forEach(code => {
        const el = code as HTMLElement;
        el.style.backgroundColor = '#f3f4f6';
        el.style.padding = '2px 6px';
        el.style.fontFamily = 'Consolas, Monaco, monospace';
        el.style.fontSize = '9pt';
        el.style.color = '#1f2937';
        el.style.borderRadius = '0';
      });

      // 处理引用块
      const blockquotes = clonedContent.querySelectorAll('blockquote');
      blockquotes.forEach(bq => {
        (bq as HTMLElement).style.borderLeft = '4px solid #d1d5db';
        (bq as HTMLElement).style.paddingLeft = '16px';
        (bq as HTMLElement).style.marginTop = '12pt';
        (bq as HTMLElement).style.marginBottom = '12pt';
        (bq as HTMLElement).style.color = '#4b5563';
        (bq as HTMLElement).style.fontStyle = 'italic';
        (bq as HTMLElement).style.fontSize = '10.5pt';
        (bq as HTMLElement).style.fontFamily = 'Arial, sans-serif';
        (bq as HTMLElement).style.lineHeight = '1.6';
      });

      // 处理标题 - MarkdownRenderer 使用 h1.text-xl.font-semibold 等
      // Word 需要 mso- 前缀样式来正确识别字体大小和间距
      const h1s = clonedContent.querySelectorAll('h1');
      h1s.forEach(h => {
        h.style.fontWeight = 'bold';
        h.style.fontSize = '18pt';
        h.style.fontFamily = 'Arial, sans-serif';
        h.style.color = '#111827';
        h.style.marginTop = '24pt';
        h.style.marginBottom = '12pt';
        h.style.lineHeight = '1.4';
        h.setAttribute(
          'style',
          h.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 24pt; mso-margin-bottom-alt: 12pt;'
        );
      });

      const h2s = clonedContent.querySelectorAll('h2');
      h2s.forEach(h => {
        h.style.fontWeight = 'bold';
        h.style.fontSize = '14pt';
        h.style.fontFamily = 'Arial, sans-serif';
        h.style.color = '#111827';
        h.style.marginTop = '18pt';
        h.style.marginBottom = '10pt';
        h.style.lineHeight = '1.4';
        h.setAttribute(
          'style',
          h.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 18pt; mso-margin-bottom-alt: 10pt;'
        );
      });

      const h3s = clonedContent.querySelectorAll('h3');
      h3s.forEach(h => {
        h.style.fontWeight = 'bold';
        h.style.fontSize = '12pt';
        h.style.fontFamily = 'Arial, sans-serif';
        h.style.color = '#111827';
        h.style.marginTop = '14pt';
        h.style.marginBottom = '8pt';
        h.style.lineHeight = '1.4';
        h.setAttribute(
          'style',
          h.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 14pt; mso-margin-bottom-alt: 8pt;'
        );
      });

      const h4s = clonedContent.querySelectorAll('h4, h5, h6');
      h4s.forEach(h => {
        const el = h as HTMLElement;
        el.style.fontWeight = 'bold';
        el.style.fontSize = '10.5pt';
        el.style.fontFamily = 'Arial, sans-serif';
        el.style.color = '#111827';
        el.style.marginTop = '12pt';
        el.style.marginBottom = '6pt';
        el.style.lineHeight = '1.4';
        el.setAttribute(
          'style',
          el.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 12pt; mso-margin-bottom-alt: 6pt;'
        );
      });

      // 处理列表
      const lists = clonedContent.querySelectorAll('ul, ol');
      lists.forEach(list => {
        (list as HTMLElement).style.marginTop = '6pt';
        (list as HTMLElement).style.marginBottom = '6pt';
        (list as HTMLElement).style.paddingLeft = '24pt';
      });

      const listItems = clonedContent.querySelectorAll('li');
      listItems.forEach(li => {
        li.style.fontSize = '10.5pt';
        li.style.fontFamily = 'Arial, sans-serif';
        li.style.color = '#374151';
        li.style.lineHeight = '1.8';
        li.style.marginTop = '3pt';
        li.style.marginBottom = '3pt';
      });

      // 处理链接
      const links = clonedContent.querySelectorAll('a');
      links.forEach(a => {
        a.style.color = '#2563eb';
        a.style.textDecoration = 'underline';
      });

      // 移除不需要的元素（如圆角、阴影等纯装饰性样式）
      // 同时移除复制按钮等交互元素
      const copyButtons = clonedContent.querySelectorAll('button');
      copyButtons.forEach(btn => btn.remove());

      const allElements = clonedContent.querySelectorAll('*');
      allElements.forEach(el => {
        const htmlEl = el as HTMLElement;
        // 只移除装饰性样式，保留功能性样式
        htmlEl.style.borderRadius = '0';
        htmlEl.style.boxShadow = 'none';
      });

      const htmlContent = clonedContent.innerHTML;
      const plainText = paperRef.current.innerText
        .replace(/Page Break/g, '') // 从纯文本中也移除 Page Break
        .replace(/Copy\nCopied!/g, '') // 移除复制按钮文字
        .replace(/\n{3,}/g, '\n\n'); // 清理多余空行

      // 创建包含样式的完整 HTML，专门为 Word 优化
      // 使用 mso- 前缀样式确保 Word 正确识别格式
      const styledHtml = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns:m="http://schemas.microsoft.com/office/2004/12/omml">
          <head>
            <meta charset="utf-8">
            <!--[if gte mso 9]>
            <xml>
              <o:OfficeDocumentSettings>
                <o:AllowPNG/>
              </o:OfficeDocumentSettings>
            </xml>
            <![endif]-->
            <style>
              /* Word 兼容样式 */
              body {
                font-family: Arial, sans-serif;
                font-size: 10.5pt;
                line-height: 1.8;
                color: #374151;
                mso-line-height-rule: exactly;
              }
              h1 {
                font-size: 18pt;
                font-weight: bold;
                color: #111827;
                margin-top: 24pt;
                margin-bottom: 12pt;
                mso-margin-top-alt: 24pt;
                mso-margin-bottom-alt: 12pt;
                mso-style-priority: 9;
              }
              h2 {
                font-size: 14pt;
                font-weight: bold;
                color: #111827;
                margin-top: 18pt;
                margin-bottom: 10pt;
                mso-margin-top-alt: 18pt;
                mso-margin-bottom-alt: 10pt;
                mso-style-priority: 9;
              }
              h3 {
                font-size: 12pt;
                font-weight: bold;
                color: #111827;
                margin-top: 14pt;
                margin-bottom: 8pt;
                mso-margin-top-alt: 14pt;
                mso-margin-bottom-alt: 8pt;
                mso-style-priority: 9;
              }
              h4, h5, h6 {
                font-size: 10.5pt;
                font-weight: bold;
                color: #111827;
                margin-top: 12pt;
                margin-bottom: 6pt;
                mso-margin-top-alt: 12pt;
                mso-margin-bottom-alt: 6pt;
                mso-style-priority: 9;
              }
              p {
                margin-top: 6pt;
                margin-bottom: 6pt;
                line-height: 1.8;
                mso-margin-top-alt: 6pt;
                mso-margin-bottom-alt: 6pt;
                mso-line-height-rule: exactly;
                text-align: justify;
              }
              ul, ol {
                margin-top: 6pt;
                margin-bottom: 6pt;
                padding-left: 24pt;
                mso-margin-top-alt: 6pt;
                mso-margin-bottom-alt: 6pt;
              }
              li {
                margin-top: 3pt;
                margin-bottom: 3pt;
                line-height: 1.8;
                mso-margin-top-alt: 3pt;
                mso-margin-bottom-alt: 3pt;
              }
              table {
                border-collapse: collapse;
                margin-top: 12pt;
                margin-bottom: 12pt;
                mso-margin-top-alt: 12pt;
                mso-margin-bottom-alt: 12pt;
              }
              th {
                background-color: #f3f4f6;
                font-weight: bold;
                padding: 8px;
                border: 1px solid #d1d5db;
                font-size: 10.5pt;
                color: #1f2937;
                mso-background-themecolor: background1;
                mso-background-themeshade: 242;
              }
              td {
                padding: 8px;
                border: 1px solid #d1d5db;
                font-size: 10.5pt;
                color: #374151;
              }
              code {
                font-family: Consolas, 'Courier New', monospace;
                background-color: #f3f4f6;
                padding: 2px 4px;
                font-size: 9pt;
                mso-highlight: #f3f4f6;
              }
              pre {
                font-family: Consolas, 'Courier New', monospace;
                background-color: #f8f8f8;
                padding: 12px;
                white-space: pre-wrap;
                font-size: 9pt;
                margin-top: 12pt;
                margin-bottom: 12pt;
                border: 1px solid #d1d5db;
                mso-margin-top-alt: 12pt;
                mso-margin-bottom-alt: 12pt;
                mso-highlight: #f8f8f8;
              }
              blockquote {
                border-left: 4px solid #d1d5db;
                padding-left: 16px;
                margin-top: 12pt;
                margin-bottom: 12pt;
                color: #4b5563;
                font-style: italic;
                mso-margin-top-alt: 12pt;
                mso-margin-bottom-alt: 12pt;
              }
              a {
                color: #2563eb;
                text-decoration: underline;
              }
            </style>
          </head>
          <body>${htmlContent}</body>
        </html>
      `;

      // 使用 Clipboard API 同时写入 HTML 和纯文本
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([styledHtml], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // 降级到纯文本复制
      if (paperRef.current) {
        const text = paperRef.current.innerText;
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  }, []);

  // 监听滚动，更新当前活动标题
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleScroll = () => {
      const headings = container.querySelectorAll('[id^="heading-"]');
      let currentId = '';

      headings.forEach(heading => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) {
          currentId = heading.id;
        }
      });

      if (currentId && currentId !== activeHeadingId) {
        setActiveHeadingId(currentId);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeHeadingId]);

  // 监听手动复制事件，添加 Word 兼容的内联样式
  useEffect(() => {
    const paper = paperRef.current;
    if (!paper) return;

    const handleCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      // 获取选中的内容
      const range = selection.getRangeAt(0);
      const fragment = range.cloneContents();

      // 创建临时容器
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(fragment);

      // 移除 Page Break 标记
      const allDivs = tempDiv.querySelectorAll('div');
      allDivs.forEach(div => {
        if (div.textContent?.includes('Page Break')) {
          div.remove();
        }
      });
      const spans = tempDiv.querySelectorAll('span');
      spans.forEach(span => {
        if (span.textContent?.includes('Page Break')) {
          const parent = span.closest('div');
          if (parent) {
            parent.remove();
          } else {
            span.remove();
          }
        }
      });

      // ===== 处理 CodeBlock 组件结构 =====
      // CodeBlock 组件结构: div.my-3.rounded-lg > div.bg-gray-100 (header) + SyntaxHighlighter
      // 需要将其转换为 Word 可识别的简单 pre 结构
      const codeBlockWrappers = tempDiv.querySelectorAll(
        'div.rounded-lg.overflow-hidden'
      );
      codeBlockWrappers.forEach(wrapper => {
        const wrapperEl = wrapper as HTMLElement;
        // 检查是否是代码块容器（有 border 类）
        if (
          wrapperEl.classList.contains('border') ||
          wrapperEl.className.includes('border-gray')
        ) {
          // 获取代码内容 - 在 SyntaxHighlighter 渲染的 pre > code 中
          const codeEl = wrapperEl.querySelector('pre code');
          const preEl = wrapperEl.querySelector('pre');
          if (codeEl || preEl) {
            const codeText = codeEl?.textContent || preEl?.textContent || '';
            // 获取语言标签
            const langSpan = wrapperEl.querySelector(
              '.bg-gray-100 span, div > span.text-xs'
            );
            const language = langSpan?.textContent?.trim() || 'text';

            // 创建 Word 兼容的代码块结构
            const newPre = document.createElement('pre');
            newPre.style.backgroundColor = '#f8f8f8';
            newPre.style.border = '1px solid #d1d5db';
            newPre.style.borderRadius = '0';
            newPre.style.padding = '12px';
            newPre.style.marginTop = '12pt';
            newPre.style.marginBottom = '12pt';
            newPre.style.fontFamily = 'Consolas, Monaco, monospace';
            newPre.style.fontSize = '9pt';
            newPre.style.lineHeight = '1.5';
            newPre.style.whiteSpace = 'pre-wrap';
            newPre.style.wordBreak = 'break-all';
            newPre.style.color = '#1f2937';
            newPre.style.overflowX = 'auto';

            // 添加语言标签作为注释
            if (language && language !== 'text') {
              const langComment = document.createElement('div');
              langComment.style.backgroundColor = '#e5e7eb';
              langComment.style.padding = '4px 12px';
              langComment.style.fontSize = '9pt';
              langComment.style.fontFamily = 'Arial, sans-serif';
              langComment.style.fontWeight = 'bold';
              langComment.style.color = '#4b5563';
              langComment.style.marginBottom = '0';
              langComment.style.borderBottom = '1px solid #d1d5db';
              langComment.textContent = language.toUpperCase();
              wrapperEl.parentNode?.insertBefore(langComment, wrapperEl);
            }

            const newCode = document.createElement('code');
            newCode.style.fontFamily = 'Consolas, Monaco, monospace';
            newCode.style.fontSize = '9pt';
            newCode.style.backgroundColor = 'transparent';
            newCode.textContent = codeText;

            newPre.appendChild(newCode);
            wrapperEl.replaceWith(newPre);
          }
        }
      });

      // 移除复制按钮等交互元素
      const copyButtons = tempDiv.querySelectorAll('button');
      copyButtons.forEach(btn => btn.remove());

      // 将 Tailwind CSS 类转换为 Word 兼容的内联样式
      // 处理表格
      const tables = tempDiv.querySelectorAll('table');
      tables.forEach(table => {
        table.setAttribute('border', '1');
        table.setAttribute('cellpadding', '8');
        table.setAttribute('cellspacing', '0');
        table.setAttribute('align', 'center');
        table.style.borderCollapse = 'collapse';
        table.style.marginTop = '12pt';
        table.style.marginBottom = '12pt';
      });

      // 处理表头单元格
      const thCells = tempDiv.querySelectorAll('th');
      thCells.forEach(th => {
        th.style.backgroundColor = '#f3f4f6';
        th.style.fontWeight = 'bold';
        th.style.padding = '8px';
        th.style.border = '1px solid #d1d5db';
        th.style.fontSize = '10.5pt';
        th.style.fontFamily = 'Arial, sans-serif';
        th.style.color = '#1f2937';
      });

      // 处理表格数据单元格
      const tdCells = tempDiv.querySelectorAll('td');
      tdCells.forEach(td => {
        td.style.padding = '8px';
        td.style.border = '1px solid #d1d5db';
        td.style.fontSize = '10.5pt';
        td.style.fontFamily = 'Arial, sans-serif';
        td.style.color = '#374151';
        td.style.lineHeight = '1.6';
      });

      // 处理表格行的交替背景色
      const tbodyRows = tempDiv.querySelectorAll('tbody tr');
      tbodyRows.forEach((tr, index) => {
        if (index % 2 === 1) {
          (tr as HTMLElement).style.backgroundColor = '#f9fafb';
        }
      });

      // 处理段落 - MarkdownRenderer 使用 div.mb-3 而不是 p
      // 将段落 div 转换为 p 标签，以获得更好的 Word 兼容性
      const paragraphDivs = tempDiv.querySelectorAll('div');
      paragraphDivs.forEach(div => {
        const el = div as HTMLElement;
        // 检查是否是段落容器（有 mb-3 或 leading-relaxed 类）
        if (
          el.classList.contains('mb-3') ||
          el.classList.contains('leading-relaxed')
        ) {
          // 排除特殊容器（代码块、表格等）
          if (
            !el.closest('pre') &&
            !el.closest('table') &&
            !el.closest('blockquote') &&
            !el.querySelector('pre') &&
            !el.querySelector('table') &&
            !el.querySelector('div') // 排除有嵌套 div 的容器
          ) {
            // 将 div 转换为 p 标签
            const p = document.createElement('p');
            p.innerHTML = el.innerHTML;
            p.style.fontSize = '10.5pt';
            p.style.fontFamily = 'Arial, sans-serif';
            p.style.color = '#374151';
            p.style.lineHeight = '1.8';
            p.style.marginTop = '6pt';
            p.style.marginBottom = '6pt';
            p.style.textAlign = 'justify';
            // 添加 mso 样式属性以确保 Word 识别
            p.setAttribute(
              'style',
              p.getAttribute('style') +
                '; mso-margin-top-alt: 6pt; mso-margin-bottom-alt: 6pt; mso-line-height-rule: exactly;'
            );
            el.replaceWith(p);
          }
        }
      });

      // 处理 p 标签（包括原有的和刚转换的）
      const paragraphs = tempDiv.querySelectorAll('p');
      paragraphs.forEach(p => {
        const el = p as HTMLElement;
        if (!el.style.fontSize) {
          el.style.fontSize = '10.5pt';
          el.style.fontFamily = 'Arial, sans-serif';
          el.style.color = '#374151';
          el.style.lineHeight = '1.8';
          el.style.marginTop = '6pt';
          el.style.marginBottom = '6pt';
          el.style.textAlign = 'justify';
          el.setAttribute(
            'style',
            el.getAttribute('style') +
              '; mso-margin-top-alt: 6pt; mso-margin-bottom-alt: 6pt; mso-line-height-rule: exactly;'
          );
        }
      });

      // 处理代码块 - 包括已转换的和原生的
      const codeBlocks = tempDiv.querySelectorAll('pre');
      codeBlocks.forEach(pre => {
        // 如果还没有设置样式（不是从 CodeBlock 转换来的）
        if (!pre.style.backgroundColor) {
          pre.style.backgroundColor = '#f8f8f8';
          pre.style.padding = '12px';
          pre.style.fontFamily = 'Consolas, Monaco, monospace';
          pre.style.fontSize = '9pt';
          pre.style.whiteSpace = 'pre-wrap';
          pre.style.border = '1px solid #d1d5db';
          pre.style.color = '#1f2937';
          pre.style.lineHeight = '1.5';
          pre.style.marginTop = '12pt';
          pre.style.marginBottom = '12pt';
        }
      });

      // 处理内联代码 - MarkdownRenderer 使用 code.bg-gray-100
      const inlineCodes = tempDiv.querySelectorAll('code:not(pre code)');
      inlineCodes.forEach(code => {
        const el = code as HTMLElement;
        el.style.backgroundColor = '#f3f4f6';
        el.style.padding = '2px 6px';
        el.style.fontFamily = 'Consolas, Monaco, monospace';
        el.style.fontSize = '9pt';
        el.style.color = '#1f2937';
        el.style.borderRadius = '0';
      });

      // 处理引用块
      const blockquotes = tempDiv.querySelectorAll('blockquote');
      blockquotes.forEach(bq => {
        (bq as HTMLElement).style.borderLeft = '4px solid #d1d5db';
        (bq as HTMLElement).style.paddingLeft = '16px';
        (bq as HTMLElement).style.marginTop = '12pt';
        (bq as HTMLElement).style.marginBottom = '12pt';
        (bq as HTMLElement).style.color = '#4b5563';
        (bq as HTMLElement).style.fontStyle = 'italic';
        (bq as HTMLElement).style.fontSize = '10.5pt';
        (bq as HTMLElement).style.fontFamily = 'Arial, sans-serif';
        (bq as HTMLElement).style.lineHeight = '1.6';
      });

      // 处理标题 - 添加 Word 兼容的 mso- 样式
      const h1s = tempDiv.querySelectorAll('h1');
      h1s.forEach(h => {
        h.style.fontWeight = 'bold';
        h.style.fontSize = '18pt';
        h.style.fontFamily = 'Arial, sans-serif';
        h.style.color = '#111827';
        h.style.marginTop = '24pt';
        h.style.marginBottom = '12pt';
        h.style.lineHeight = '1.4';
        h.setAttribute(
          'style',
          h.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 24pt; mso-margin-bottom-alt: 12pt;'
        );
      });

      const h2s = tempDiv.querySelectorAll('h2');
      h2s.forEach(h => {
        h.style.fontWeight = 'bold';
        h.style.fontSize = '14pt';
        h.style.fontFamily = 'Arial, sans-serif';
        h.style.color = '#111827';
        h.style.marginTop = '18pt';
        h.style.marginBottom = '10pt';
        h.style.lineHeight = '1.4';
        h.setAttribute(
          'style',
          h.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 18pt; mso-margin-bottom-alt: 10pt;'
        );
      });

      const h3s = tempDiv.querySelectorAll('h3');
      h3s.forEach(h => {
        h.style.fontWeight = 'bold';
        h.style.fontSize = '12pt';
        h.style.fontFamily = 'Arial, sans-serif';
        h.style.color = '#111827';
        h.style.marginTop = '14pt';
        h.style.marginBottom = '8pt';
        h.style.lineHeight = '1.4';
        h.setAttribute(
          'style',
          h.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 14pt; mso-margin-bottom-alt: 8pt;'
        );
      });

      const h4s = tempDiv.querySelectorAll('h4, h5, h6');
      h4s.forEach(h => {
        const el = h as HTMLElement;
        el.style.fontWeight = 'bold';
        el.style.fontSize = '10.5pt';
        el.style.fontFamily = 'Arial, sans-serif';
        el.style.color = '#111827';
        el.style.marginTop = '12pt';
        el.style.marginBottom = '6pt';
        el.style.lineHeight = '1.4';
        el.setAttribute(
          'style',
          el.getAttribute('style') +
            '; mso-style-priority: 9; mso-margin-top-alt: 12pt; mso-margin-bottom-alt: 6pt;'
        );
      });

      // 处理列表
      const lists = tempDiv.querySelectorAll('ul, ol');
      lists.forEach(list => {
        (list as HTMLElement).style.marginTop = '6pt';
        (list as HTMLElement).style.marginBottom = '6pt';
        (list as HTMLElement).style.paddingLeft = '24pt';
      });

      const listItems = tempDiv.querySelectorAll('li');
      listItems.forEach(li => {
        li.style.fontSize = '10.5pt';
        li.style.fontFamily = 'Arial, sans-serif';
        li.style.color = '#374151';
        li.style.lineHeight = '1.8';
        li.style.marginTop = '3pt';
        li.style.marginBottom = '3pt';
      });

      // 处理链接
      const links = tempDiv.querySelectorAll('a');
      links.forEach(a => {
        a.style.color = '#2563eb';
        a.style.textDecoration = 'underline';
      });

      // 移除装饰性样式
      const allElements = tempDiv.querySelectorAll('*');
      allElements.forEach(el => {
        const htmlEl = el as HTMLElement;
        htmlEl.style.borderRadius = '0';
        htmlEl.style.boxShadow = 'none';
      });

      // 创建 Word 兼容的 HTML，使用 mso- 前缀样式
      const styledHtml = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns:m="http://schemas.microsoft.com/office/2004/12/omml">
          <head>
            <meta charset="utf-8">
            <!--[if gte mso 9]>
            <xml>
              <o:OfficeDocumentSettings>
                <o:AllowPNG/>
              </o:OfficeDocumentSettings>
            </xml>
            <![endif]-->
            <style>
              body { font-family: Arial, sans-serif; font-size: 10.5pt; line-height: 1.8; color: #374151; mso-line-height-rule: exactly; }
              h1 { font-size: 18pt; font-weight: bold; color: #111827; margin-top: 24pt; margin-bottom: 12pt; mso-margin-top-alt: 24pt; mso-margin-bottom-alt: 12pt; mso-style-priority: 9; }
              h2 { font-size: 14pt; font-weight: bold; color: #111827; margin-top: 18pt; margin-bottom: 10pt; mso-margin-top-alt: 18pt; mso-margin-bottom-alt: 10pt; mso-style-priority: 9; }
              h3 { font-size: 12pt; font-weight: bold; color: #111827; margin-top: 14pt; margin-bottom: 8pt; mso-margin-top-alt: 14pt; mso-margin-bottom-alt: 8pt; mso-style-priority: 9; }
              h4, h5, h6 { font-size: 10.5pt; font-weight: bold; color: #111827; margin-top: 12pt; margin-bottom: 6pt; mso-margin-top-alt: 12pt; mso-margin-bottom-alt: 6pt; mso-style-priority: 9; }
              p { margin-top: 6pt; margin-bottom: 6pt; line-height: 1.8; mso-margin-top-alt: 6pt; mso-margin-bottom-alt: 6pt; mso-line-height-rule: exactly; text-align: justify; }
              ul, ol { margin-top: 6pt; margin-bottom: 6pt; padding-left: 24pt; mso-margin-top-alt: 6pt; mso-margin-bottom-alt: 6pt; }
              li { margin-top: 3pt; margin-bottom: 3pt; line-height: 1.8; mso-margin-top-alt: 3pt; mso-margin-bottom-alt: 3pt; }
              table { border-collapse: collapse; margin-top: 12pt; margin-bottom: 12pt; mso-margin-top-alt: 12pt; mso-margin-bottom-alt: 12pt; }
              th { background-color: #f3f4f6; font-weight: bold; padding: 8px; border: 1px solid #d1d5db; font-size: 10.5pt; color: #1f2937; mso-background-themecolor: background1; mso-background-themeshade: 242; }
              td { padding: 8px; border: 1px solid #d1d5db; font-size: 10.5pt; color: #374151; }
              code { font-family: Consolas, 'Courier New', monospace; background-color: #f3f4f6; padding: 2px 4px; font-size: 9pt; mso-highlight: #f3f4f6; }
              pre { font-family: Consolas, 'Courier New', monospace; background-color: #f8f8f8; padding: 12px; white-space: pre-wrap; font-size: 9pt; margin-top: 12pt; margin-bottom: 12pt; border: 1px solid #d1d5db; mso-margin-top-alt: 12pt; mso-margin-bottom-alt: 12pt; mso-highlight: #f8f8f8; }
              blockquote { border-left: 4px solid #d1d5db; padding-left: 16px; margin-top: 12pt; margin-bottom: 12pt; color: #4b5563; font-style: italic; mso-margin-top-alt: 12pt; mso-margin-bottom-alt: 12pt; }
              a { color: #2563eb; text-decoration: underline; }
            </style>
          </head>
          <body>${tempDiv.innerHTML}</body>
        </html>
      `;

      // 阻止默认复制行为，写入格式化后的内容
      e.preventDefault();
      e.clipboardData?.setData('text/html', styledHtml);
      e.clipboardData?.setData(
        'text/plain',
        tempDiv.innerText.replace(/Page Break/g, '').replace(/\n{3,}/g, '\n\n')
      );
    };

    paper.addEventListener('copy', handleCopy);
    return () => paper.removeEventListener('copy', handleCopy);
  }, []);

  // A4 尺寸（像素，基于 96 DPI）
  // Portrait: 210mm x 297mm ≈ 794px x 1123px
  // Landscape: 297mm x 210mm ≈ 1123px x 794px
  const paperWidth = layout === 'portrait' ? 794 : 1123;

  return (
    <div className="flex h-full bg-gray-100">
      {/* 侧边栏 - 目录 */}
      <div
        className={cn(
          'flex-shrink-0 bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden',
          isSidebarOpen ? 'w-80' : 'w-0'
        )}
      >
        <div className="h-full flex flex-col w-80">
          {/* 侧边栏标题 */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900 truncate" title={filename}>
              {filename}
            </h3>
          </div>

          {/* 目录列表 */}
          <div className="flex-1 overflow-y-auto p-2">
            {toc.length > 0 ? (
              toc.map(item => (
                <TocItemComponent
                  key={item.id}
                  item={item}
                  activeId={activeHeadingId}
                  onNavigate={handleNavigate}
                />
              ))
            ) : (
              <div className="text-sm text-gray-500 px-2 py-4">
                No headings found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 工具栏 */}
        <div className="flex-shrink-0 h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          {/* 左侧：侧边栏切换 + 文件名 */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              <Menu size={18} className="text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
              {filename}
            </span>
          </div>

          {/* 中间：缩放控制 */}
          <div className="flex items-center gap-0">
            <button
              onClick={() => setZoom(Math.max(50, zoom - 10))}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Zoom out"
              disabled={zoom <= 50}
            >
              <Minus
                size={16}
                className={zoom <= 50 ? 'text-gray-300' : 'text-gray-600'}
              />
            </button>
            <span className="text-sm text-gray-700 w-12 text-center">
              {zoom}%
            </span>
            <button
              onClick={() => setZoom(Math.min(200, zoom + 10))}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Zoom in"
              disabled={zoom >= 200}
            >
              <Plus
                size={16}
                className={zoom >= 200 ? 'text-gray-300' : 'text-gray-600'}
              />
            </button>
          </div>

          {/* 右侧：操作按钮和布局切换 */}
          <div className="flex items-center gap-2">
            {/* 分页符配置按钮 */}
            <Tooltip
              content="Treat horizontal rules as page breaks"
              position="bottom"
            >
              <button
                onClick={() => setTreatHrAsPageBreak(!treatHrAsPageBreak)}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  treatHrAsPageBreak
                    ? 'text-blue-600 hover:bg-blue-50'
                    : 'text-gray-400 hover:bg-gray-100'
                )}
              >
                <img
                  src="/static/page-break-s.svg"
                  alt="Page break setting"
                  className="w-[18px] h-[18px]"
                  style={{
                    filter: treatHrAsPageBreak
                      ? 'invert(29%) sepia(98%) saturate(1234%) hue-rotate(199deg) brightness(95%) contrast(92%)'
                      : 'invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(90%)',
                  }}
                />
              </button>
            </Tooltip>

            {/* 复制按钮 */}
            <Tooltip content="Copy for a Word document." position="bottom">
              <button
                onClick={handleCopyFormatted}
                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              >
                {copied ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <img
                    src="/static/copy-word.svg"
                    alt="Copy for Word"
                    className="w-[18px] h-[18px]"
                  />
                )}
              </button>
            </Tooltip>

            {/* 下载 Word 文档按钮 */}
            <Tooltip content="Export as a Word document." position="bottom">
              <button
                onClick={handleDownloadWord}
                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              >
                <img
                  src="/static/word-export.svg"
                  alt="Export to Word"
                  className="w-[18px] h-[18px]"
                />
              </button>
            </Tooltip>

            {/* 布局切换 */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setLayout('portrait')}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  layout === 'portrait'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                )}
                title="Portrait (A4)"
              >
                {/* 纵向图标 */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <rect x="4" y="2" width="8" height="12" rx="1" />
                </svg>
              </button>
              <button
                onClick={() => setLayout('landscape')}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  layout === 'landscape'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                )}
                title="Landscape (A4)"
              >
                {/* 横向图标 */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <rect x="2" y="4" width="12" height="8" rx="1" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* 内容区域 */}
        <div ref={contentRef} className="flex-1 overflow-auto p-8 bg-gray-100">
          {/* A4 纸张容器 */}
          <div
            ref={paperRef}
            className="mx-auto bg-white shadow-lg"
            style={{
              width: `${paperWidth * (zoom / 100)}px`,
              minHeight:
                layout === 'portrait'
                  ? `${1123 * (zoom / 100)}px`
                  : `${794 * (zoom / 100)}px`,
              padding: `${40 * (zoom / 100)}px ${60 * (zoom / 100)}px`,
              transformOrigin: 'top center',
            }}
          >
            <div
              style={{
                fontSize: `${14 * (zoom / 100)}px`,
                lineHeight: 1.8,
              }}
            >
              <MarkdownRenderer
                content={processedContent}
                treatHrAsPageBreak={treatHrAsPageBreak}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
